import { useEffect, useRef, useState } from 'react';
import { Alert } from '@lmig/lmds-react-alert';
import { IconButton, IconChat, IconSend } from '@lmig/lmds-react-icons';
import { Textarea } from '@lmig/lmds-react-textarea';
import { Caption, Heading } from '@lmig/lmds-react-typography';
import { requestAIAnswer } from 'Feature/Coveo/Helpers/AIAnswerApi';
import { renderFormattedAnswer } from './AIAnswerContent';
import './AIChat.scss';

type ChatRole = 'user' | 'assistant';

interface IChatMessage {
  id: number;
  role: ChatRole;
  text: string;
}

interface IAIChatProps {
  /**
   * The search the summary above answered. It is the first turn of the AI Runtime conversation
   * thread, so a new one means a new subject and the visible transcript starts over with it.
   */
  topic: string;
}

// The AI Runtime accepts up to 5000 characters, but a follow-up is a sentence or two - a tighter
// limit keeps the composer honest without ever reaching the server's own rejection.
const MAX_QUESTION_LENGTH = 1000;

const AUTHOR_LABELS: Record<ChatRole, string> = {
  user: 'You',
  assistant: 'AI assistant',
};

export function AIChat({ topic }: IAIChatProps) {
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const nextMessageId = useRef(0);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);

  // Bumped whenever the conversation restarts, so an in-flight answer that belongs to the previous
  // subject can be recognised and dropped instead of landing in the new transcript.
  const conversationGeneration = useRef(0);

  useEffect(() => {
    conversationGeneration.current += 1;
    setMessages([]);
    setDraft('');
    setChatError(null);
    setIsSending(false);
  }, [topic]);

  useEffect(() => {
    if (messages.length === 0 && !isSending) return;
    // scrollIntoView is not implemented in jsdom, so it is called only when the browser provides it.
    transcriptEndRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [messages, isSending]);

  const sendQuestion = async () => {
    const question = draft.trim();
    if (!question || isSending) return;

    const sentGeneration = conversationGeneration.current;
    const isSuperseded = () => conversationGeneration.current !== sentGeneration;

    setDraft('');
    setChatError(null);
    setMessages((current) => [...current, { id: nextMessageId.current++, role: 'user', text: question }]);
    setIsSending(true);

    try {
      const answer = await requestAIAnswer(question, isSuperseded);
      if (!answer) return;

      setMessages((current) => [...current, { id: nextMessageId.current++, role: 'assistant', text: answer.answer }]);
    } catch {
      if (isSuperseded()) return;
      setChatError('We were unable to answer that follow-up. Please try again.');
    } finally {
      if (!isSuperseded()) {
        setIsSending(false);
      }
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends so the composer behaves like a chat box; Shift+Enter still adds a line break.
    if (event.nativeEvent.isComposing) return;

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendQuestion();
    }
  };

  const canSend = draft.trim().length > 0 && !isSending;

  return (
    <div className="ai-chat">
      <div className="ai-chat-header">
        <span className="ai-chat-avatar" aria-hidden="true">
          <IconChat size="16" color="inverse" />
        </span>
        <Heading type="h6-bold" tag="h3" className="ai-chat-title">
          Ask a follow-up
        </Heading>
      </div>

      {messages.length === 0 && !isSending && (
        <Caption className="ai-chat-intro">
          This conversation continues from the summary above, so you can refer back to it.
        </Caption>
      )}

      {/* A chat transcript is a sequence of new entries rather than a replaced status, so it is a
          polite log: assistive technology hears each new message without the whole thread being
          re-read every time. */}
      <div className="ai-chat-transcript" role="log" aria-live="polite" aria-label="Follow-up conversation">
        {messages.map((message) => (
          <div key={message.id} className={`ai-chat-message ai-chat-message--${message.role}`}>
            <span className="ai-chat-author">{AUTHOR_LABELS[message.role]}</span>
            <div className="ai-chat-bubble">
              {message.role === 'assistant' ? renderFormattedAnswer(message.text) : <p>{message.text}</p>}
            </div>
          </div>
        ))}

        {isSending && (
          <div className="ai-chat-message ai-chat-message--assistant">
            <span className="ai-chat-author">{AUTHOR_LABELS.assistant}</span>
            <div className="ai-chat-bubble ai-chat-bubble--pending">
              <span className="ai-chat-typing" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              Thinking…
            </div>
          </div>
        )}

        <div ref={transcriptEndRef} />
      </div>

      {chatError && (
        <Alert className="ai-chat-error" highlightType="negative" compact>
          {chatError}
        </Alert>
      )}

      <form
        className="ai-chat-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void sendQuestion();
        }}
      >
        {/* LMDS puts `className` on the inner <textarea>, so the field is wrapped to give the flex row
            something of its own to stretch. */}
        <div className="ai-chat-field">
          <Textarea
            className="ai-chat-input"
            labelA11y="Ask a follow-up question about this summary"
            placeholder="Ask a follow-up question…"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            maxLength={MAX_QUESTION_LENGTH}
            rows={2}
            resize="none"
            disabled={isSending}
          />
        </div>
        <IconButton
          className="ai-chat-send"
          type="submit"
          background="teal"
          disabled={!canSend}
          aria-label="Send follow-up"
        >
          {/* No color override: the teal IconButton drives the icon fill itself (Liberty Blue at rest,
              white on hover), so forcing one leaves it invisible against the resting background. */}
          <IconSend size="16" aria-hidden="true" />
        </IconButton>
      </form>
    </div>
  );
}
